import { Link } from "react-router-dom";
import { useState } from "react";
import axios, { API } from "@/lib/api";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import { formatApiErrorDetail } from "@/lib/authForms";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      // Backend always returns 200 (anti-enumeration) — we mirror that in UI.
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, "Could not send reset email."));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <AuthShell
        title="Check your inbox"
        subtitle={`If an account exists for ${email}, we just sent a reset link. It expires in 60 minutes.`}
        footer={
          <Link to="/signin" data-testid="forgot-back-signin" className="text-[#D4AF37] hover:text-[#F3CE5A]">
            ← Back to sign in
          </Link>
        }
      >
        <div className="mt-8 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-5 text-center" data-testid="forgot-sent">
          <CheckCircle2 className="w-8 h-8 text-emerald-300 mx-auto mb-2" />
          <p className="text-sm text-white/80">Email sent (if the account exists).</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Forgot password"
      subtitle="Enter your email and we'll send a one-time link to reset your password."
      footer={
        <Link to="/signin" className="text-[#D4AF37] hover:text-[#F3CE5A]">← Back to sign in</Link>
      }
    >
      <form onSubmit={submit} className="mt-7 space-y-4" data-testid="forgot-form">
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              data-testid="forgot-email-input"
              className="w-full bg-[#141414] border border-white/10 rounded-xl pl-9 pr-3 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              placeholder="you@studio.com"
            />
          </div>
        </div>
        {error && (
          <p data-testid="forgot-error" className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting || !email}
          data-testid="forgot-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3.5 hover:bg-[#F3CE5A] transition disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)]"
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send reset link</>}
        </button>
      </form>
    </AuthShell>
  );
}
